import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import KanbanColumn from './KanbanColumn';
import KanbanTask from './KanbanTask';
import kanbanApi from '../services/kanbanApi';

const initialColumns = {
  'todo': {
    id: 'todo',
    title: 'To Do',
    taskIds: []
  },
  'in-progress': {
    id: 'in-progress',
    title: 'In Progress',
    taskIds: []
  },
  'review': {
    id: 'review',
    title: 'Review',
    taskIds: []
  },
  'done': {
    id: 'done',
    title: 'Done',
    taskIds: []
  }
};

const initialTasks = {};

const KanbanBoard = () => {
  const [tasks, setTasks] = useState(initialTasks);
  const [columns, setColumns] = useState(initialColumns);
  const [activeId, setActiveId] = useState(null);
  const [dragStartContainer, setDragStartContainer] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load data from API on component mount
  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const tasksFromApi = await kanbanApi.getAllTasks();
      
      // Convert array of tasks to tasks object and organize by columns
      const tasksObj = {};
      const newColumns = { ...initialColumns };
      
      tasksFromApi.forEach(task => {
        tasksObj[task.id] = task;
        const columnId = task.status || 'todo';
        if (newColumns[columnId] && !newColumns[columnId].taskIds.includes(task.id)) {
          newColumns[columnId].taskIds.push(task.id);
        }
      });
      
      setTasks(tasksObj);
      setColumns(newColumns);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addTask = async () => {
    if (!newTaskTitle.trim()) return;

    try {
      const newTask = {
        title: newTaskTitle,
        assignee: newTaskAssignee || 'Unassigned',
        status: 'todo',
        description: '',
        createdAt: new Date().toISOString()
      };

      const createdTask = await kanbanApi.createTask(newTask);
      
      setTasks(prev => ({
        ...prev,
        [createdTask.id]: createdTask
      }));

      setColumns(prev => ({
        ...prev,
        'todo': {
          ...prev['todo'],
          taskIds: [...prev['todo'].taskIds, createdTask.id]
        }
      }));

      setNewTaskTitle('');
      setNewTaskAssignee('');
      setShowAddTask(false);
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await kanbanApi.deleteTask(taskId);
      
      // Remove task from tasks
      setTasks(prev => {
        const newTasks = { ...prev };
        delete newTasks[taskId];
        return newTasks;
      });

      // Remove task from all columns
      setColumns(prev => {
        const newColumns = { ...prev };
        Object.keys(newColumns).forEach(columnId => {
          newColumns[columnId].taskIds = newColumns[columnId].taskIds.filter(id => id !== taskId);
        });
        return newColumns;
      });
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const updatedTask = await kanbanApi.updateTask(taskId, updates);
      
      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          ...updatedTask
        }
      }));
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task. Please try again.');
    }
  };

  function handleDragStart(event) {
    const activeId = event.active.id;
    setActiveId(activeId);
    
    // Capture the starting container
    const startContainer = findContainer(activeId);
    setDragStartContainer(startContainer);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Find the containers
    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) {
      return;
    }

    // If we're moving between different containers
    if (activeContainer !== overContainer) {
      setColumns(prev => {
        const activeItems = prev[activeContainer].taskIds;
        const overItems = prev[overContainer].taskIds;

        // Find the indexes for the items
        const activeIndex = activeItems.indexOf(activeId);
        let newIndex;

        if (overId in prev) {
          // We're dropping on the column itself
          newIndex = overItems.length;
        } else {
          // We're dropping on a specific task
          const overIndex = overItems.indexOf(overId);
          newIndex = overIndex >= 0 ? overIndex : overItems.length;
        }

        return {
          ...prev,
          [activeContainer]: {
            ...prev[activeContainer],
            taskIds: activeItems.filter(id => id !== activeId)
          },
          [overContainer]: {
            ...prev[overContainer],
            taskIds: [
              ...overItems.slice(0, newIndex),
              activeId,
              ...overItems.slice(newIndex)
            ]
          }
        };
      });
    }
  }

  async function handleDragEnd(event) {
    console.log('🎬 handleDragEnd called!', event);
    const { active, over } = event;

    console.log('🎯 Active:', active?.id, 'Over:', over?.id);

    if (!over) {
      console.log('❌ No over target, exiting');
      setActiveId(null);
      setDragStartContainer(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Use the original container from drag start
    const activeContainer = dragStartContainer;
    
    // Find the final container where the task was dropped
    const finalContainer = findContainer(activeId);
    
    console.log('📦 Containers - Original:', activeContainer, 'Final:', finalContainer);

    // If we moved to a different container, update the task status
    if (activeContainer !== finalContainer) {

    if (!activeContainer || !finalContainer) {
      setActiveId(null);
      setDragStartContainer(null);
      return;
    }

      console.log('🔄 Moving task from', activeContainer, 'to', finalContainer);
      console.log('🎯 Task ID:', activeId);
      
      try {
        // Call API to update task status
        console.log('📡 Calling API to update task status...');
        const updatedTask = await kanbanApi.updateTask(activeId, { status: finalContainer });
        console.log('✅ API call successful:', updatedTask);
        
        // Update the task in local state with new status
        setTasks(prev => ({
          ...prev,
          [activeId]: {
            ...prev[activeId],
            status: finalContainer
          }
        }));

        console.log('✅ Local state updated');
        
      } catch (err) {
        console.error('❌ Error updating task status:', err);
        setError('Failed to update task status. Please try again.');
        // Revert the UI change by reloading tasks from API
        loadTasks();
        setActiveId(null);
        setDragStartContainer(null);
        return;
      }
    } else {
      console.log('📍 Task stayed in same container:', activeContainer);
    }

    // Reordering within the same container is handled by handleDragOver
    // No additional logic needed here for same-container moves

    setActiveId(null);
    setDragStartContainer(null);
  }

  function findContainer(id) {
    console.log('🔍 findContainer called with id:', id);
    console.log('📋 Available columns:', Object.keys(columns));
    console.log('📋 Column taskIds:', Object.keys(columns).map(key => ({ [key]: columns[key].taskIds })));
    
    if (id in columns) {
      console.log('✅ Found as column:', id);
      return id;
    }

    const foundContainer = Object.keys(columns).find(key => 
      columns[key].taskIds.includes(id)
    );
    
    console.log('🎯 Found container for task:', foundContainer);
    return foundContainer;
  }

  const activeTask = activeId ? tasks[activeId] : null;

  return (
    <div className="container">
      <div className="kanban-header">
        <h1>Task Board</h1>
        <button 
          className="add-task-btn"
          onClick={() => setShowAddTask(!showAddTask)}
        >
          + Add Task
        </button>
      </div>

      {showAddTask && (
        <div className="add-task-form">
          <input
            type="text"
            placeholder="Task title..."
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
          />
          <input
            type="text"
            placeholder="Assignee (optional)..."
            value={newTaskAssignee}
            onChange={(e) => setNewTaskAssignee(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
          />
          <div className="form-actions">
            <button onClick={addTask}>Add</button>
            <button onClick={() => setShowAddTask(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-message">
          Loading tasks...
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToWindowEdges]}
      >
        <div className="kanban-board">
          {Object.values(columns).map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={column.taskIds.map(taskId => tasks[taskId]).filter(Boolean)}
              onDeleteTask={deleteTask}
              onUpdateTask={updateTask}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask ? (
            <KanbanTask 
              task={activeTask} 
              isDragging={true}
              onDelete={() => {}}
              onUpdate={() => {}}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <div className="band-logo">
        <img src="/lm.png" alt="Band Logo" style={{ maxWidth: '300px', marginTop: '40px' }} />
      </div>
    </div>
  );
};

export default KanbanBoard;